import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { IMAGES } from "@/lib/images";

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (user) {
      const dest = localStorage.getItem("postLoginRedirect") || "/dashboard";
      localStorage.removeItem("postLoginRedirect");
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  const handleGoogle = () => {
    const dest = localStorage.getItem("postLoginRedirect") || "/dashboard";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + dest;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left — form */}
      <div className="flex flex-col grain">
        <div className="px-5 sm:px-8 py-6">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="login-back">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backHome")}
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center px-5 pb-24">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm" data-testid="login-card">
            <Brand size="xl" to={null} />
            <h1 className="mt-6 font-heading text-3xl tracking-tight text-primary">{t("auth.welcomeTitle")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("auth.loginSubtitle")}</p>

            <Button onClick={handleGoogle} className="mt-8 w-full rounded-sm bg-card text-foreground border border-border hover:bg-secondary h-11 card-soft" data-testid="login-google-btn">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
              </svg>
              {t("auth.continueGoogle")}
            </Button>

            <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-orange-600" /> {t("auth.secureNote")}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right — image */}
      <div className="relative hidden lg:block">
        <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/30 to-primary/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-3xl leading-tight">{t("auth.sideTitle")}</p>
          <p className="mt-3 text-white/70 max-w-sm">{t("auth.sideBlurb")}</p>
        </div>
      </div>
    </div>
  );
}
